import { CardContent } from "@/components";
import { useQueryUrl } from "@/services/queryUtils";
import { FilterParams } from "@/services/revenueApi";
import { ItemPlatformsResponse, MetricSplit, SelectedItem } from "@/types/content";
import { keepPreviousData } from "@tanstack/react-query";
import { Button, ConfigProvider, Flex, theme, Typography } from "antd";
import Table, { ColumnType } from "antd/es/table";
import { useMemo, useState } from "react";
import { getMetricColumns } from "./metricColumns";

type SelectedItemWithMetrics = SelectedItem & MetricSplit
type ExpandedMetric = { label: string } & MetricSplit

const LOAD_MORE_COUNT = 5;
const INITIAL_PLATFORM_COUNT = 5;

export type ItemPlatformParams<TRow extends SelectedItemWithMetrics> = {
  item: TRow;
  filters: FilterParams;
  limit: number;
};

type ExpandedRowRenderParams<TRow extends SelectedItemWithMetrics> = {
  item: TRow;
  filters: FilterParams;
  parentTableColumns: Array<ColumnType<TRow>>;
  urlBuilder: (params: ItemPlatformParams<TRow>) => string;
}

/**
 * Infers child columns from the parent table, placing App Name + metrics in the same
 * positions as Publish date / metric columns.
 */
function getColumns<TRow extends SelectedItemWithMetrics>(parentColumns: Array<ColumnType<TRow>>) {
  // Match the parent's video visibility so audio-only rows also drop the percentage.
  const showVideoMetrics = parentColumns.some(({ dataIndex }) => dataIndex === 'videoViews');
  const metricColumnsByKey = new Map(
    getMetricColumns<ExpandedMetric>('episode', { showVideoMetrics }).map((column) => [
      String(column.dataIndex),
      column,
    ])
  );

  // Parent may show all metric columns or only a subset (video columns hidden without HLS shows).
  const index = parentColumns.findIndex(({ dataIndex }) =>
    metricColumnsByKey.has(String(dataIndex))
  );

  if (index === -1) {
    throw new Error(
      'ExpandedMetricRowRender only works with metric columns included. Please use ...getMetricColumns() in the parent table'
    );
  }

  // Mirror the parent layout: 'App Name' sits directly before the metric columns, and each
  // metric column takes the parent's width so the sub-table stays aligned.
  const columns: Array<ColumnType<ExpandedMetric>> = parentColumns.map(
    (parentColumn, columnIndex) => {
      const width = parentColumn.width !== undefined ? { width: parentColumn.width } : {};

      if (columnIndex === index - 1) {
        return {
          title: 'App Name',
          dataIndex: 'label',
          ellipsis: true,
          ...width,
          render: (label: string) => (
            <Typography.Text ellipsis={{ tooltip: label }}>{label}</Typography.Text>
          ),
        };
      }

      const metricColumn = metricColumnsByKey.get(String(parentColumn.dataIndex));
      return metricColumn ? { ...metricColumn, ...width } : width;
    }
  );

  return columns.map((column, index) => ({
    ...column,
    ...(index === columns.length - 1 ? { width: 48 } : {}), // align with parent Table.EXPAND_COLUMN width
    onCell: () => ({ style: { background: 'transparent' } }),
  }));
}

/**
 * Used to expand rows on the Content Performance Page tables
 * Creates a headerless sub-table with the Metric Columns in the same position as the parent
 * Used for showing which apps are used for a given Episode/Show/Network
 */
export function ExpandedMetricRowRenderEcho<TRow extends SelectedItemWithMetrics>({
  item,
  urlBuilder,
  parentTableColumns,
  filters,
}: ExpandedRowRenderParams<TRow>) {
  const [limit, setLimit] = useState(INITIAL_PLATFORM_COUNT);
  const query = useQueryUrl<ItemPlatformsResponse, ItemPlatformParams<TRow>>(
    urlBuilder,
    { item, filters, limit },
    { placeholderData: keepPreviousData }
  );

  const platforms = (query.data?.platforms ?? []).slice(0, limit);
  const total = query.data?.total ?? 0;

  return (
    <CardContent<ItemPlatformsResponse>
      {...query}
      variant="echo"
      renderData={() => (
        <ExpandedPlatformsSection
          platforms={platforms}
          parentTableColumns={parentTableColumns}
          total={total}
          limit={limit}
          isPlaceholderData={Boolean(query.isPlaceholderData)}
          onLimitChange={setLimit}
        />
      )}
    />
  );
}

const ExpandedPlatformsSection = <TRow extends SelectedItemWithMetrics>({
  platforms,
  parentTableColumns,
  total,
  limit,
  isPlaceholderData,
  onLimitChange,
}: {
  platforms: ExpandedMetric[];
  parentTableColumns: Array<ColumnType<TRow>>;
  total: number;
  limit: number;
  isPlaceholderData: boolean;
  onLimitChange: (nextLimit: number) => void;
}) => {
  const { token } = theme.useToken();

  const hasMore = total > platforms.length;
  const remaining = Math.min(LOAD_MORE_COUNT, total - platforms.length);
  const canShowFewer = limit > INITIAL_PLATFORM_COUNT;

  const columns = useMemo(
    () => getColumns(parentTableColumns),
    [parentTableColumns]
  );

  const platformsBg = token.colorPrimaryBg;
  return (
    <div
      data-testid="expanded-platforms-section"
      style={{ backgroundColor: platformsBg }}
      className="[&_.ant-table-cell]:!border-0"
    >
      <ConfigProvider
        theme={{
          components: {
            Table: {
              rowHoverBg: 'transparent',
            },
          },
        }}
      >
        <Table
          size="small"
          showHeader={false}
          dataSource={platforms}
          rowKey="label"
          columns={columns}
          pagination={false}
          styles={{
            root: { background: 'transparent' },
            content: { background: 'transparent' },
            body: {
              cell: { paddingInline: token.padding, background: platformsBg },
            },
          }}
        />
      </ConfigProvider>
      {total > 0 && (
        <Flex
          align="center"
          style={{
            position: 'relative',
            paddingInline: token.padding,
            paddingBlock: token.paddingSM,
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            (Showing {platforms.length} of {total})
          </Typography.Text>
          <Flex
            vertical
            align="center"
            gap={0}
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {hasMore && !isPlaceholderData && (
              <Button
                type="link"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => onLimitChange(limit + LOAD_MORE_COUNT)}
              >
                {`+ Show ${remaining} more apps`}
              </Button>
            )}
            {canShowFewer && (
              <Button
                type="link"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => onLimitChange(INITIAL_PLATFORM_COUNT)}
              >
                - Show fewer apps
              </Button>
            )}
          </Flex>
        </Flex>
      )}
    </div>
  );
};
